// =====================================================================
// ComptageFormModal.jsx - Create/edit InventoryCount (M-004 B2.1b)
// =====================================================================
// Pattern: TrajetFormModal.jsx ca referinta.
//
// Props:
//   - countId: null = create, string = edit existing
//   - onClose: () => void
//   - onSaved: () => void (apelat dupa create/update/delete reusite)
//
// Highlight: SearchableItemInput - search-as-you-type pe items.
//   User tasteaza, vede max 8 results, click selecteaza.
//   ↑↓ navigate, Enter select, Escape close dropdown.
// =====================================================================

import { useState, useEffect, useRef } from 'react';
import {
  inventoryItems as itemsApi,
  inventoryCounts as countsApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';

const today = () => new Date().toISOString().slice(0, 10);

const COUNT_TYPE_LABELS_FR = {
  MID_MONTH: 'Mi-mois',
  END_MONTH: 'Fin mois',
  AD_HOC:    'Ad-hoc',
};

// Inline styles
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: '#fff',
  borderRadius: 8,
  padding: '1.5rem 1.75rem',
  width: '90%',
  maxWidth: 560,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.375rem',
};

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.625rem',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: '0.875rem',
  background: '#fff',
  boxSizing: 'border-box',
};

const btnPrimary = {
  background: '#2563eb', color: '#fff', border: 0,
  padding: '0.5rem 1.25rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const btnSecondary = {
  background: 'transparent', color: '#6b7280',
  border: '1px solid #d1d5db', padding: '0.5rem 1.25rem',
  borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const btnDanger = {
  background: 'transparent', color: '#b91c1c',
  border: '1px solid #fca5a5', padding: '0.5rem 1.25rem',
  borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

// =====================================================================
// MAIN MODAL
// =====================================================================
export default function ComptageFormModal({ countId, onClose, onSaved }) {
  const { user, can } = useAuth();
  const isEdit = !!countId;

  const canCreate = can('inventory_counts.create.all') || can('inventory_counts.create.own');
  const canUpdate = can('inventory_counts.update.all');
  const canUpdateOwn = can('inventory_counts.update.own');
  const canDelete = can('inventory_counts.delete.all');

  // Form state
  const [date, setDate]               = useState(today());
  const [selectedItem, setSelectedItem] = useState(null); // {id, ref, name, unit, project: {name}}
  const [quantity, setQuantity]       = useState('');
  const [notes, setNotes]             = useState('');

  // Meta (edit-only)
  const [ref, setRef]                 = useState('');
  const [countType, setCountType]     = useState('');
  const [invoiced, setInvoiced]       = useState(false);
  const [countedByName, setCounter]   = useState('');
  const [countedByUserId, setCountedById] = useState(null);

  // UI state
  const [loading, setLoading]         = useState(isEdit);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [allItems, setAllItems]       = useState([]); // for search

  // Load all items (active) + count if edit
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Load all active items pentru search
        const itemsRes = await itemsApi.list({ isActive: 'true', pageSize: 500 });
        if (cancelled) return;
        setAllItems(itemsRes.items || []);

        if (isEdit) {
          const c = await countsApi.get(countId);
          if (cancelled) return;
          setRef(c.ref);
          setDate((c.date || '').slice(0, 10));
          setSelectedItem(c.item);
          setQuantity(String(c.quantity));
          setNotes(c.notes || '');
          setCountType(c.countType);
          setInvoiced(c.invoiced);
          setCounter(c.countedBy?.name || '');
          setCountedById(c.countedByUserId || c.countedBy?.id || null);
        }
      } catch (err) {
        console.error('[ComptageFormModal] load:', err);
        setError(err.response?.data?.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [countId, isEdit]);

  // Escape closes modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e?.preventDefault();
    setError(null);

    // Validation
    if (!date) return setError('Date requise');
    if (!selectedItem) return setError('Item requis');
    if (quantity === '' || isNaN(parseFloat(quantity))) return setError('Quantité requise (nombre)');
    if (parseFloat(quantity) < 0) return setError('Quantité doit être >= 0');

    setSaving(true);
    try {
      const data = {
        date,
        itemId: selectedItem.id,
        quantity: parseFloat(quantity),
        notes: notes.trim() || null,
      };
      if (isEdit) {
        await countsApi.update(countId, data);
      } else {
        await countsApi.create(data);
      }
      onSaved();
    } catch (err) {
      console.error('[ComptageFormModal] save:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Supprimer le comptage ${ref} ?`)) return;
    setSaving(true);
    try {
      await countsApi.remove(countId);
      onSaved();
    } catch (err) {
      console.error('[ComptageFormModal] delete:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
      setSaving(false);
    }
  }

  // Day 11++ (01-06-2026) — own-scoped edit: update.all OU (propriétaire + update.own), tant que NON facturé.
  const isOwnCount = !!countedByUserId && countedByUserId === user?.id;
  const canEditThis = canUpdate || (isOwnCount && canUpdateOwn);
  // Disable form si invoiced sau (édition d'un comptage que je ne peux pas modifier)
  const formDisabled = invoiced || saving || (isEdit && !canEditThis);

  if (loading) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div className="modal-mobile-fullscreen" style={modalStyle} onClick={e => e.stopPropagation()}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div className="modal-mobile-fullscreen" style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            {isEdit ? 'Modifier comptage' : 'Nouveau comptage'}
          </h2>
          {isEdit && (
            <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>{ref}</span>
          )}
        </div>

        {invoiced && (
          <div style={{ background: '#fef3c7', color: '#92400e', padding: '0.625rem 0.875rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem' }}>
            ⚠ Ce comptage est déjà facturé. Les modifications et suppression sont bloquées.
          </div>
        )}

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.625rem 0.875rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Date */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Date du comptage *</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              style={inputStyle}
              disabled={formDisabled}
              required
            />
            <small style={{ color: '#9ca3af', fontSize: '0.6875rem' }}>
              Le type (Mi-mois / Fin mois / Ad-hoc) est dérivé automatiquement.
            </small>
          </div>

          {/* Item search-as-you-type */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Item *</label>
            <SearchableItemInput
              items={allItems}
              selected={selectedItem}
              onSelect={setSelectedItem}
              disabled={formDisabled || isEdit /* Don't allow item change on edit */}
            />
            {isEdit && (
              <small style={{ color: '#9ca3af', fontSize: '0.6875rem' }}>
                L'item ne peut pas être changé en édition. Supprimez et recréez si nécessaire.
              </small>
            )}
          </div>

          {/* Quantity */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>
              Quantité * {selectedItem && <span style={{ color: '#9ca3af', textTransform: 'none', fontWeight: 400 }}>({selectedItem.unit})</span>}
            </label>
            <input
              type="number"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              style={inputStyle}
              min="0"
              step="0.01"
              max="999999"
              disabled={formDisabled}
              required
              placeholder="0"
            />
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
              maxLength={500}
              disabled={formDisabled}
              placeholder="Observation, contexte..."
            />
          </div>

          {/* Edit metadata */}
          {isEdit && (
            <div style={{ background: '#f9fafb', padding: '0.75rem 0.875rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.75rem', color: '#6b7280' }}>
              <div>Type: <strong>{COUNT_TYPE_LABELS_FR[countType] || countType}</strong></div>
              <div>Encodé par: <strong>{countedByName}</strong></div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
            <div>
              {isEdit && canDelete && !invoiced && (
                <button type="button" style={btnDanger} onClick={handleDelete} disabled={saving}>
                  Supprimer
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={btnSecondary} onClick={onClose} disabled={saving}>
                Annuler
              </button>
              {(canCreate || (isEdit && canEditThis && !invoiced)) && (
                <button type="submit" style={btnPrimary} disabled={saving || invoiced}>
                  {saving ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =====================================================================
// SearchableItemInput - search-as-you-type pe items
// =====================================================================
function SearchableItemInput({ items, selected, onSelect, disabled }) {
  const [query, setQuery]     = useState('');
  const [isOpen, setIsOpen]   = useState(false);
  const [activeIdx, setIdx]   = useState(0);
  const containerRef          = useRef(null);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, []);

  // Filter items
  const q = query.trim().toLowerCase();
  const filtered = !q ? items.slice(0, 8) : items.filter(it => {
    return (it.name?.toLowerCase().includes(q)) ||
           (it.ref?.toLowerCase().includes(q)) ||
           (it.project?.name?.toLowerCase().includes(q));
  }).slice(0, 8);

  function handleSelect(item) {
    onSelect(item);
    setQuery('');
    setIsOpen(false);
    setIdx(0);
  }

  function handleKeyDown(e) {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIdx(i => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIdx]) handleSelect(filtered[activeIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Selected display */}
      {selected && !isOpen ? (
        <div style={{
          padding: '0.5rem 0.625rem',
          border: '1px solid #d1d5db',
          borderRadius: 4,
          background: disabled ? '#f9fafb' : '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }} onClick={() => !disabled && setIsOpen(true)}>
          <div>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
              {selected.name}
            </div>
            <div style={{ fontSize: '0.6875rem', color: '#9ca3af', fontFamily: 'monospace' }}>
              {selected.ref} · {selected.project?.name} · {selected.unit}
            </div>
          </div>
          {!disabled && (
            <button
              type="button"
              style={{ background: 'transparent', border: 0, color: '#9ca3af', cursor: 'pointer', fontSize: '1rem', padding: '0 0.25rem' }}
              onClick={e => { e.stopPropagation(); onSelect(null); setQuery(''); setIsOpen(true); }}
              title="Désélectionner"
            >×</button>
          )}
        </div>
      ) : (
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setIsOpen(true); setIdx(0); }}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="🔍 Rechercher item par nom, réf ou projet..."
          style={inputStyle}
          disabled={disabled}
          autoFocus={!selected}
        />
      )}

      {/* Dropdown */}
      {isOpen && !selected && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#fff',
          border: '1px solid #d1d5db',
          borderRadius: 4,
          marginTop: 2,
          maxHeight: 280,
          overflowY: 'auto',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {filtered.length === 0 && (
            <div style={{ padding: '0.75rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.8125rem' }}>
              Aucun item trouvé
            </div>
          )}
          {filtered.map((it, idx) => (
            <div
              key={it.id}
              onClick={() => handleSelect(it)}
              onMouseEnter={() => setIdx(idx)}
              style={{
                padding: '0.5rem 0.625rem',
                cursor: 'pointer',
                background: idx === activeIdx ? '#dbeafe' : 'transparent',
                borderBottom: idx < filtered.length - 1 ? '1px solid #f3f4f6' : 'none',
              }}
            >
              <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                {it.name}
              </div>
              <div style={{ fontSize: '0.6875rem', color: '#6b7280', fontFamily: 'monospace' }}>
                {it.ref} · {it.project?.name} · {it.unit}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
